import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Loader2, AlertTriangle } from 'lucide-react';
import { NodeStatusBadge } from '@/components/node-status-badge';

export interface NodeChange {
  key: string;
  label: string;
  oldValue: boolean;
  newValue: boolean;
}

interface NodeChangesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: NodeChange[];
  onSave: () => void;
  isSaving?: boolean;
}

export const NodeChangesModal = ({
  open,
  onOpenChange,
  changes,
  onSave,
  isSaving = false,
}: NodeChangesModalProps) => {
  return (
    <Modal
      title='Save changes'
      description='Review the changes to node configuration before saving.'
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      contentClassName='max-w-2xl'
      footer={
        <>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving || changes.length === 0}>
            {isSaving && <Loader2 className='mr-2 size-4 animate-spin' />}
            Save changes
          </Button>
        </>
      }
    >
      <div className='space-y-4'>
        <div>
          <h3 className='text-sm font-medium mb-2'>Changes</h3>
          <div className='border rounded-md'>
            {changes.map((change, index) => (
              <div
                key={change.key}
                className={`flex items-center justify-between p-3 ${
                  index !== changes.length - 1 ? 'border-b' : ''
                }`}
              >
                <div className='flex-1'>
                  <p className='text-sm font-medium'>{change.label}</p>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {change.key}
                  </p>
                </div>
                <div className='flex items-center gap-2'>
                  <NodeStatusBadge enabled={change.oldValue} />
                  <span className='text-muted-foreground'>→</span>
                  <NodeStatusBadge enabled={change.newValue} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {changes.length === 0 && (
          <div className='flex items-center gap-2 p-4 bg-muted rounded-md'>
            <AlertTriangle className='size-4 text-muted-foreground' />
            <p className='text-sm text-muted-foreground'>No changes to save</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
